import {
  Button,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Space,
  Statistic,
  Table,
  Typography,
  Upload,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { UploadOutlined, SendOutlined, FileAddOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import CompanyPoliciesView from "../components/CompanyPoliciesView";
import {
  addCompanyPolicy,
  getCompanyPolicyAcknowledgments,
  previewCompanyPolicy,
  sendPolicyReminder,
} from "../repository/data.repository";
import { getProblemMessage } from "../utils/http";
import { QueryOperation } from "../code/search-query/search-query";

// PD-02 §6.15 — Policies surface. Company users can:
//  • Browse / preview the active policy library (existing grid).
//  • Publish a new policy by uploading its PDF + metadata (modal).
//  • Monitor who has acknowledged a policy and nudge the rest (modal).
function CompanyPoliciesPage() {
  const [publishOpen, setPublishOpen] = useState(false);
  const [monitorOpen, setMonitorOpen] = useState(false);
  const [monitorPolicy, setMonitorPolicy] = useState<{ uid: string; name: string } | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  function onPolicyAction(
    mode: "preview" | "monitor",
    values: { readonly uid: string; readonly name?: string },
  ): void {
    if (mode === "preview") {
      Promise.resolve(previewCompanyPolicy(values.uid)).catch((e: any) => {
        message.error(getProblemMessage(e) || "Failed to preview policy.");
      });
    } else if (mode === "monitor") {
      setMonitorPolicy({ uid: values.uid, name: values.name ?? "" });
      setMonitorOpen(true);
    }
  }

  return (
    <Col className="relative flex flex-auto flex-col overflow-hidden overflow-y-auto">
      <Row className="sticky top-0 z-10 flex flex-row flex-wrap items-center p-4">
        <Typography.Title className="m-0 flex-auto" level={4}>
          Policies
        </Typography.Title>
        <Space>
          <Button
            type="primary"
            icon={<FileAddOutlined />}
            onClick={() => setPublishOpen(true)}
          >
            Publish Policy
          </Button>
        </Space>
      </Row>
      <Col className="flex flex-auto flex-col overflow-hidden p-4 pb-4 pt-0">
        <CompanyPoliciesView
          key={refreshKey}
          onAction={(key: string, record: any) =>
            onPolicyAction(key as any, record)
          }
        />
      </Col>

      <PublishPolicyModal
        open={publishOpen}
        onClose={() => setPublishOpen(false)}
        onPublished={() => {
          setPublishOpen(false);
          setRefreshKey((k) => k + 1);
        }}
      />

      <AcknowledgmentMonitorModal
        open={monitorOpen}
        policy={monitorPolicy}
        onClose={() => setMonitorOpen(false)}
      />
    </Col>
  );
}

function PublishPolicyModal(props: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onPublished: () => void;
}) {
  const [form] = Form.useForm();
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);

  function onSubmit() {
    form
      .validateFields()
      .then(({ name, code, description }) => {
        if (!file) {
          message.error("Attach the policy PDF before publishing.");
          return;
        }
        setSubmitting(true);
        addCompanyPolicy({ name, code, description, pdfFile: file })
          .then(() => {
            message.success(`Policy "${name}" published.`);
            form.resetFields();
            setFile(null);
            props.onPublished();
          })
          .catch((err) =>
            message.error(getProblemMessage(err) || "Failed to publish policy."),
          )
          .finally(() => setSubmitting(false));
      })
      .catch(() => {
        /* validation errors shown inline */
      });
  }

  return (
    <Modal
      open={props.open}
      title="Publish policy"
      onCancel={props.onClose}
      footer={[
        <Button key="cancel" onClick={props.onClose}>
          Cancel
        </Button>,
        <Button key="publish" type="primary" loading={submitting} onClick={onSubmit}>
          Publish
        </Button>,
      ]}
      width={560}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="name"
          label="Policy name"
          rules={[{ required: true, message: "Name is required" }]}
        >
          <Input placeholder="e.g. Workplace Harassment Prevention 2026" />
        </Form.Item>
        <Form.Item
          name="code"
          label="Policy code"
          rules={[{ required: true, message: "Code is required" }]}
        >
          <Input placeholder="e.g. HR-2026-004" />
        </Form.Item>
        <Form.Item name="description" label="Short description (optional)">
          <Input.TextArea rows={3} placeholder="One or two lines so employees know what this policy is about" />
        </Form.Item>
        <Form.Item label="Policy PDF" required>
          <Upload
            accept=".pdf"
            maxCount={1}
            fileList={file ? [{ uid: "pdf", name: file.name, status: "done" }] : []}
            beforeUpload={(f) => {
              setFile(f);
              return false;
            }}
            onRemove={() => setFile(null)}
          >
            <Button icon={<UploadOutlined />}>
              {file ? "Replace PDF" : "Choose PDF"}
            </Button>
          </Upload>
        </Form.Item>
        <Typography.Text type="secondary" className="text-xs">
          Publishing commits the policy for the whole company. Employees see
          it in their app immediately and the mandatory "I Acknowledge" step
          kicks in.
        </Typography.Text>
      </Form>
    </Modal>
  );
}

function AcknowledgmentMonitorModal(props: {
  readonly open: boolean;
  readonly policy: { uid: string; name: string } | null;
  readonly onClose: () => void;
}) {
  const [acks, setAcks] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!props.open || !props.policy) {
      setAcks([]);
      return;
    }
    setLoading(true);
    getCompanyPolicyAcknowledgments({
      searchQuery: {
        conditions: [
          {
            field: "policyUid",
            values: [props.policy.uid],
            operation: QueryOperation.Equal,
          },
        ],
      },
      pageNumber: 0,
      pageSize: 500,
      sortBy: "acknowledgedAt",
      sortDescending: true,
    })
      .then((data: any) => setAcks(data?.data ?? []))
      .catch((err) =>
        message.error(getProblemMessage(err) || "Failed to load acknowledgments."),
      )
      .finally(() => setLoading(false));
  }, [props.open, props.policy]);

  function onSendReminder() {
    if (!props.policy) return;
    setSending(true);
    sendPolicyReminder(props.policy.uid)
      .then((count) => {
        message.success(
          count === 0
            ? "Everyone has already acknowledged."
            : `Reminder queued for ${count} user${count === 1 ? "" : "s"}.`,
        );
      })
      .catch((err) =>
        message.error(getProblemMessage(err) || "Failed to send reminders."),
      )
      .finally(() => setSending(false));
  }

  const columns: ColumnsType<any> = [
    {
      title: "User",
      dataIndex: "userUserName",
      render: (v: string, row: any) => {
        const full = [row.userFirstName, row.userLastName]
          .filter(Boolean)
          .join(" ");
        return full ? `${full} (${v})` : v;
      },
    },
    { title: "Department", dataIndex: "departmentName" },
    {
      title: "Acknowledged",
      dataIndex: "acknowledgedAt",
      render: (v: string) => dayjs(v).format("DD/MM/YYYY HH:mm"),
    },
  ];

  return (
    <Modal
      open={props.open}
      title={`Acknowledgments — ${props.policy?.name ?? ""}`}
      onCancel={props.onClose}
      width={720}
      footer={[
        <Button key="cancel" onClick={props.onClose}>
          Close
        </Button>,
        <Button
          key="remind"
          type="primary"
          icon={<SendOutlined />}
          loading={sending}
          onClick={onSendReminder}
        >
          Send Reminder to Pending
        </Button>,
      ]}
    >
      <Row gutter={16} className="mb-3">
        <Col flex="1 1 160px">
          <Statistic title="Acknowledged" value={acks.length} />
        </Col>
      </Row>
      <Table
        rowKey="uid"
        size="small"
        loading={loading}
        dataSource={acks}
        columns={columns}
        pagination={{ pageSize: 20, showSizeChanger: false }}
        locale={{ emptyText: "No acknowledgments yet." }}
      />
      <Typography.Text type="secondary" className="mt-2 block text-xs">
        Reminder uses the employee's in-app notifications. Narrower audiences
        (specific departments / users) can be targeted via the admin UI if
        broad reminders are too noisy.
      </Typography.Text>
    </Modal>
  );
}

export default CompanyPoliciesPage;
